// @ts-check

/**
 * @typedef {{
 *   canView: boolean,
 *   canAddToCart: boolean,
 *   needsVerification: boolean,
 *   needsLogin: boolean,
 *   priceSegment: 'retail'|'educator'|'school',
 * }} EntitlementViewModel
 */

/**
 * Derives complete UI entitlement from the 3-field auth tuple.
 *
 * Rule: UI components NEVER inspect user.role, user.customerType, or
 * user.educatorVerification.status directly. They call this adapter and
 * read the derived boolean flags.
 *
 * @param {({ role?: string, customerType?: string, educatorVerification?: { status?: string } })|null|undefined} user
 * @param {boolean} [educatorOnly] - true if the product/content requires educator verification
 * @returns {EntitlementViewModel}
 */
export const adaptEntitlement = (user, educatorOnly = false) => {
  // Not logged in — guests can still add to cart (guest cart flow)
  if (!user) {
    return {
      canView: !educatorOnly,
      canAddToCart: !educatorOnly,
      needsVerification: false,
      needsLogin: educatorOnly,
      priceSegment: 'retail',
    };
  }

  const role = user.role || 'user';
  const customerType = user.customerType || 'retail';
  const verificationStatus = user.educatorVerification?.status;

  // Determine price segment
  /** @type {'retail'|'educator'|'school'} */
  const priceSegment =
    customerType === 'school'
      ? 'school'
      : customerType === 'educator' && verificationStatus === 'approved'
      ? 'educator'
      : 'retail';

  // Educator-only gate
  if (educatorOnly) {
    const isVerifiedEducator =
      (customerType === 'educator' && verificationStatus === 'approved') ||
      role === 'admin';
    const isPendingVerification =
      customerType === 'educator' &&
      (verificationStatus === 'pending' || verificationStatus === 'rejected' || !verificationStatus);

    return {
      canView: isVerifiedEducator,
      canAddToCart: isVerifiedEducator,
      needsVerification: isPendingVerification || (!isVerifiedEducator && !isPendingVerification),
      needsLogin: false,
      priceSegment,
    };
  }

  // Standard (non-educator-only) product
  return {
    canView: true,
    canAddToCart: true,
    needsVerification: false,
    needsLogin: false,
    priceSegment,
  };
};
